import React, {useEffect, useState} from 'react';
import {post, get, put} from "../../utils/fetchAPI";
import {Bounce, toast} from "react-toastify";
import { FaSearch, FaChevronDown } from 'react-icons/fa';

const CreateAdmin = ({ fetchAdmins, admin, createAdmin, onClose }) => {
    const [adminData, setAdminData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        dob: '',
        gender: '',
        type: '',
        status: '',
        role: '',
    });
    const [roles, setRoles] = useState([]);
    const [filteredRoles, setFilteredRoles] = useState([]);
    const [roleSearch, setRoleSearch] = useState('');
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const [selectedRoleName, setSelectedRoleName] = useState('');

    useEffect(() => {
        const loadData = async () => {
            try {
                const response = await get('/role/list');
                const rolesList = response?.data?.roles || [];
                setRoles(rolesList);
                setFilteredRoles(rolesList);
                
                if (admin) {
                    const roleId = admin.role?._id || admin.role || '';
                    setAdminData({
                        ...admin,
                        dob: admin.dob ? new Date(admin.dob).toISOString().split('T')[0] : '',
                        role: roleId,
                    });
                    // Set selected role name after roles are loaded
                    if (roleId) {
                        const role = rolesList.find(r => r._id === roleId);
                        if (role) {
                            setSelectedRoleName(role.name);
                        }
                    }
                } else {
                    // Reset role selection when creating new admin
                    setSelectedRoleName('');
                    setAdminData(prev => ({ ...prev, role: '' }));
                }
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };
        loadData();
    }, [admin]);


    useEffect(() => {
        if (roleSearch) {
            const filtered = roles.filter(role =>
                role.name.toLowerCase().includes(roleSearch.toLowerCase())
            );
            setFilteredRoles(filtered);
        } else {
            setFilteredRoles(roles);
        }
    }, [roleSearch, roles]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setAdminData({
                ...adminData,
                [name]: value
            });
    };

    const handleRoleSelect = (role) => {
        setAdminData({
            ...adminData,
            role: role._id
        });
        setSelectedRoleName(role.name);
        setShowRoleDropdown(false);
        setRoleSearch('');
    };

    const handleRoleSearchChange = (e) => {
        setRoleSearch(e.target.value);
        setShowRoleDropdown(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const dataToSend = { ...adminData };

        // Remove password from the payload if updating an existing user
        if (admin) {
            delete dataToSend.password;
        }
        if (admin) {
            await put(`/update/${admin._id}`, dataToSend)
                .then(async (response) => {
                    toast.success('User updated successfully!', {
                        position: "top-center",
                        type: "success",
                        theme: "light",
                        autoClose: true,
                        hideProgressBar: true,
                        draggable: true,
                    });
                    createAdmin(response.data);
                    await fetchAdmins();
                    onClose();
                })
                .catch((error) => {
                    console.log("Error updating user:", error);
                    toast.error("Failed to update user.", {
                        position: "top-center",
                        type:"error",
                        theme: "light",
                        hideProgressBar: true,
                        draggable: true,
                    });
                    onClose();
                });
        } else {
            // Handle user creation (password is required for creation)
            await post('/create', adminData)
                .then(async (response) => {
                    toast.success("User created successfully!", {
                        position: "top-center",
                        theme: "light",
                        transition: Bounce,
                        hideProgressBar: false,
                        draggable: true,
                    });
                    createAdmin(response.data);
                    await fetchAdmins();
                    onClose();
                })
                .catch((error) => {
                    if (error.response && error.response.status === 409) {
                        toast.error("This email already exists. Please use a different email.", {
                            position: "top-center",
                            theme: "light",
                            hideProgressBar: true,
                            draggable: true,
                        });
                    } else {
                        console.error("Error creating user data", error);
                        toast.error("An error occurred while creating the user. Please try again.", {
                            position: "top-center",
                            theme: "light",
                            hideProgressBar: true,
                        });
                    }
                });
        }
    };


    return (
        <div className='py-6 min-[320px]:py-2 lg:px-5 min-[320px]:px-2'>
            <h2 className="text-blue-500 lg:text-3xl sm:text-lg font-bold mb-4">
                {admin ? "Edit Admin" : "Create New Admin"}</h2>
            <form className='text-black lg:text-sm min-[320px]:text-xs flex flex-col w-full min-[320px]:max-h-[30rem] overflow-y-auto' onSubmit={handleSubmit}>
                <div className="flex lg:flex-row min-[320px]:flex-col gap-5 mb-2">
                    <div className="w-full ">
                        <label>Name:</label>
                        <input
                            type="name"
                            name="name"
                            value={adminData?.name}
                            placeholder="Name"
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                            required
                        />
                    </div>
                    <div className="w-full">
                        <label>Email:</label>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={adminData?.email}
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                            required
                        />
                    </div>
                </div>
                <div className="flex gap-5 mb-2 lg:flex-row sm min-[320px]:flex-col">
                    <div className="w-full">
                        <label>Password:</label>
                        {!admin && (
                            <input
                                type="password"
                                name="password"
                                placeholder="Password"
                                value={adminData?.password}
                                onChange={handleInputChange}
                                className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                                required
                            />
                        )}

                    </div>
                    <div className=" w-full">
                        <label>Status:</label>
                        <select
                            name="status"
                            value={adminData?.status}  // Use the value from the state
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                            required >
                            <option value="" disabled>Select Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>

                    </div>
                </div>
                <div className="mb-2 flex gap-5 lg:flex-row sm min-[320px]:flex-col">
                    <div className="w-full">
                        <label>Date of Birth:</label>
                        <input
                            type="date"
                            name="dob"
                            value={adminData?.dob}
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                        />
                    </div>
                    <div className="w-full">
                        <label>Gender:</label>
                        <select
                            name="gender"
                            value={adminData?.gender}
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                        >
                            <option value="" disabled>Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                    </div>
                </div>
                <div className="mb-2 flex gap-5 lg:flex-row sm min-[320px]:flex-col">
                    <div className="w-full">
                        <label>Phone:</label>
                        <input
                            type="number"
                            name="phone"
                            placeholder="Phone Number"
                            value={adminData?.phone}
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                            required
                        />
                    </div>
                    <div className="w-full">
                        <label>Type:</label>
                        <input
                            type="text"
                            name="type"
                            placeholder="eg:admin"
                            value={adminData?.type}
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2"
                            required
                        />
                    </div>
                </div>
                <div className="mb-2 flex gap-5 lg:flex-row sm min-[320px]:flex-col">
                    <div className="w-full relative">
                        <label>Role:</label>
                        <div className="relative">
                            <div 
                                className="text-sm border p-2 rounded-md lg:w-full sm:w-1/2 flex items-center justify-between cursor-pointer bg-white"
                                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                            >
                                <span className={selectedRoleName ? 'text-black' : 'text-gray-400'}>
                                    {selectedRoleName || 'Select Role'}
                                </span>
                                <FaChevronDown className={`text-gray-400 transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
                            </div>
                            {showRoleDropdown && (
                                <div className="absolute z-10 w-full sm:w-1/2 bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-auto">
                                    <div className="p-2 border-b">
                                        <div className="relative">
                                            <FaSearch className="absolute left-2 top-2.5 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search role..."
                                                value={roleSearch}
                                                onChange={handleRoleSearchChange}
                                                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {filteredRoles.length > 0 ? (
                                            filteredRoles.map((role) => (
                                                <div
                                                    key={role._id}
                                                    onClick={() => handleRoleSelect(role)}
                                                    className={`p-2 hover:bg-gray-100 cursor-pointer text-sm ${
                                                        adminData.role === role._id ? 'bg-primary text-white hover:bg-teal-700' : ''
                                                    }`}
                                                >
                                                    {role.name}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-2 text-sm text-gray-500 text-center">
                                                No roles found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex gap-5 mt-6">
                    <button type="submit" className=" text-sm px-4 py-2 bg-blue-500 text-white rounded-md">
                        {admin?'Update' : 'Create'}
                    </button>
                    <button type="button" onClick={onClose}
                            className="text-sm px-4 py-2 bg-gray-300 text-black rounded-md">
                        Cancel
                    </button>
                </div>
            </form>

            {/* Click outside to close dropdown */}
            {showRoleDropdown && (
                <div 
                    className="fixed inset-0 z-0" 
                    onClick={() => {
                        setShowRoleDropdown(false);
                        setRoleSearch('');
                    }}
                />
            )}
        </div>
    );
};
export default CreateAdmin;
